import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { CapitalEventCreateWaterfallSchema } from "features/capital-activity/CapitalEventCreateWaterfallSchema";
import { CreateAndRunWaterfallVars } from "features/capital-activity/event/useWaterfallFundFamilySubEventIds";
import { getWaterfallBreakdownRetrieveQueryKey } from "gen/endpoints/fund-family-event/fund-family-event";
import {
  useWaterfallCreateAndRun,
  useWaterfallRunUpsert,
} from "gen/endpoints/fund-family-sub-event/fund-family-sub-event";
import { useTransactionCalculationListSuspense } from "gen/endpoints/transactions/transactions";
import { WaterfallRunPrefOnDebtDataRequest } from "gen/model/waterfallRunPrefOnDebtDataRequest";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { rootRoute } from "shared/navigation/Routes";

export interface CapitalEventCreateWaterfallModalProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  fundFamilySubEventId: string;
  fundEntityId: string;
  runWaterfall: boolean;
  isEqualizedView: boolean | undefined;
  onCloseComplete: () => void;
}

export const WaterfallAddPrefOnDebtModal: React.FC<
  CapitalEventCreateWaterfallModalProps
> = ({
  fundFamilyId,
  fundFamilyEventId,
  fundFamilySubEventId,
  fundEntityId,
  runWaterfall,
  isEqualizedView,
  onCloseComplete,
}) => {
  const { t } = useTranslation([
    "capitalCall",
    "common",
    "waterfall",
    "allocation",
  ]);
  const navigate = useNavigate();

  const { isOpen, onClose } = useDisclosure({
    defaultIsOpen: true,
  });

  const { mutateAsync: createWaterfall } = useWaterfallRunUpsert();

  const { mutateAsync: createAndRunWaterfall } = useWaterfallCreateAndRun();
  const queryClient = useQueryClient();

  const { data: transactionCalculations } =
    useTransactionCalculationListSuspense(fundFamilyId);

  const onSubmit = useCallback(
    async (data: WaterfallRunPrefOnDebtDataRequest) => {
      const createWaterfallParams: CreateAndRunWaterfallVars = {
        fundFamilyId,
        fundFamilySubEventId,
        fundEntityId,
        data: {
          pref_return_amount: data.pref_return_amount || "0",
          allocation_calculation_id: data.allocation_calculation_id,
        },
      };
      if (runWaterfall) {
        await createAndRunWaterfall(createWaterfallParams);
        await queryClient.invalidateQueries({
          queryKey: getWaterfallBreakdownRetrieveQueryKey(
            fundFamilyId,
            fundFamilyEventId,
            fundFamilySubEventId,
            fundEntityId,
            {
              is_equalized_view: isEqualizedView || undefined,
            },
          ),
        });
      } else {
        await createWaterfall({
          fundFamilyId,
          fundFamilySubEventId,
          fundEntityId,
          data: {
            pref_return_amount: data.pref_return_amount || "0",
            allocation_calculation_id: data.allocation_calculation_id,
          },
        });
      }
    },
    [
      createAndRunWaterfall,
      createWaterfall,
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
      fundEntityId,
      runWaterfall,
      isEqualizedView,
      queryClient,
    ],
  );

  const onSuccess = useCallback(() => {
    onClose();

    const waterfallRoute = rootRoute({})
      .fundFamilies({})
      .fundFamily({ fundFamilyId })
      .capitalActivity({})
      .capitalActivityEvent({ fundFamilyEventId })
      .waterfall({});

    navigate(
      runWaterfall
        ? waterfallRoute.review({ fundFamilySubEventId, fundEntityId }).$
        : waterfallRoute
            .hybrid({ fundFamilySubEventId, fundEntityId })
            .status({}).$,
    );
  }, [
    fundFamilyEventId,
    fundFamilyId,
    fundFamilySubEventId,
    fundEntityId,
    navigate,
    onClose,
    runWaterfall,
  ]);

  const form = useForm<WaterfallRunPrefOnDebtDataRequest>({
    // @ts-expect-error TODO: Convert to zod
    resolver: yupResolver(CapitalEventCreateWaterfallSchema),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit} onSuccess={onSuccess}>
          <ModalHeader>
            {t("waterfall:review.addPrefOnDebtModal.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text marginBottom={4} fontSize={14} fontWeight={400}>
              {t("waterfall:review.addPrefOnDebtModal.description")}
            </Text>
            <FormMoneyInput
              name="pref_return_amount"
              title={t(
                "waterfall:review.addPrefOnDebtModal.preferredReturnOnDebtAmount",
              )}
            />
            <FormSelect
              name="allocation_calculation_id"
              title={t("allocation:transactionCalculation")}
            >
              {transactionCalculations.map((transactionCalculation) => (
                <SelectOption
                  key={transactionCalculation.id}
                  value={transactionCalculation.id}
                >
                  {transactionCalculation.name}
                </SelectOption>
              ))}
            </FormSelect>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
